import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";

import { ExtendedTableProps } from "@/types/extendedTable";
import { SearchResult } from "@/types/helpDesk";
import { GenericTypeIndex } from "@/types/util";

import { useAuth } from "@/context/util/auth";

import ExtendedTableHead from "@/components/wrapper/extendedTableHead";

function descendingComparator<
  T extends Record<string, string | number | boolean | null | undefined>
>(a: T, b: T, orderBy: keyof T) {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  // handle undefined values safely
  if (aValue == null && bValue != null) return 1;
  if (aValue != null && bValue == null) return -1;
  if (aValue == null && bValue == null) return 0;

  // at this point, both are defined
  if (bValue! < aValue!) return -1;
  if (bValue! > aValue!) return 1;
  return 0;
}

type Order = "asc" | "desc";

function getComparator<
  T extends Record<string, string | number | boolean | null | undefined>
>(order: Order, orderBy: keyof T): (a: T, b: T) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// this method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T extends GenericTypeIndex>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function ExtendedTable<T extends GenericTypeIndex>(
  props: ExtendedTableProps<T>
) {
  const { authorization } = useAuth();
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof SearchResult>(
    props.orderBy ?? "username"
  );
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    if (props.rows.length < page * rowsPerPage) {
      setPage(0);
    }
  }, [props.rows, page, rowsPerPage, setPage]);

  const getRow = (row: T, index: number) => {
    if (!authorization) return <></>;
    return (
      <TableRow
        hover
        onClick={(event) =>
          props.onRowClick ? props.onRowClick(row, event) : null
        }
        role="checkbox"
        tabIndex={-1}
        sx={{
          cursor: props.onRowClick ? "pointer" : "default",
          ...(props.onRowClick && {
            "&:hover": {
              backgroundColor: "#58b947 !important",
            },
          }),
        }}
      >
        {props.columns.map((c, i) => {
          const value = row[c.id];
          const otherValue = c.otherId ? row[c.otherId] : null;
          return (
            <TableCell
              key={`${row[c.id]}-${index}-${i}`}
              sx={{
                ...(authorization.studentOnly &&
                  row.userIndicator !== "ST" && {
                    backgroundColor: "#d7d7d7",
                    color: "gray",
                    cursor: "not-allowed",
                  }),
                ...(authorization.k12Only &&
                  row.userIndicator !== "AF" && {
                    backgroundColor: "#d7d7d7",
                    color: "gray",
                    cursor: "not-allowed",
                  }),
              }}
            >
              {c.render ? c.render(value, otherValue) : value}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  const handleRequestSort = (property: keyof SearchResult) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.rows.length) : 0;

  return (
    <Box sx={{ width: "100%", display: props.rows.length ? "block" : "none" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <ExtendedTableHead<SearchResult>
              order={order}
              orderBy={orderBy}
              onRequestSort={(e, p) =>
                handleRequestSort(p as keyof SearchResult)
              }
              rowCount={props.rows.length}
              columns={props.columns}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(props.rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return row.tooltip ? (
                    <Tooltip
                      title={row.tooltip}
                      arrow
                      placement="top"
                      key={row.username as string}
                    >
                      {getRow(row, index)}
                    </Tooltip>
                  ) : (
                    getRow(row, index)
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={props.rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          slotProps={{
            select: {
              inputProps: { "aria-label": "rows per page" },
              native: true,
            },
          }}
        />
      </Paper>
    </Box>
  );
}
